/**
 * TabbedContent
 *
 * A component for displaying content in tabs within chat messages
 *
 * Primary responsibilities:
 * - Display multiple content sections in tabs
 * - Allow switching between tabs
 * - Support different content types in each tab
 * - Maintain responsive layout on mobile
 */

import React, { useState } from 'react'
import { cn } from '@/lib/utils'
import { IconLayoutNavbarCollapse } from '@tabler/icons-react'

interface Tab {
  id: string
  title: string
  content: string
  icon?: React.ReactNode
}

interface TabbedContentProps {
  title?: string
  tabs: Tab[]
  defaultTabId?: string
  className?: string
}

export const TabbedContent: React.FC<TabbedContentProps> = ({
  title = 'Tabbed Content',
  tabs,
  defaultTabId,
  className
}) => {
  const [activeTabId, setActiveTabId] = useState(
    defaultTabId || (tabs.length > 0 ? tabs[0].id : '')
  )

  const activeTab = tabs.find((tab) => tab.id === activeTabId) || tabs[0]

  const handleTabClick = (tabId: string) => {
    setActiveTabId(tabId)
  }

  if (tabs.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center border-b p-3">
        <div className="flex items-center gap-2">
          <IconLayoutNavbarCollapse size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{title}</h3>
        </div>
      </div>

      <div className="flex flex-col">
        {/* Tab navigation */}
        <div className="scrollbar-hide overflow-x-auto border-b">
          <div className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={cn(
                  'whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors focus:outline-none',
                  tab.id === activeTabId
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'text-muted-foreground hover:text-foreground border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                )}
              >
                <div className="flex items-center gap-1.5">
                  {tab.icon}
                  {tab.title}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="p-4">
          <div className="prose dark:prose-invert max-w-none">{activeTab?.content}</div>
        </div>
      </div>
    </div>
  )
}
